import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Users, 
  Calendar, 
  FileText, 
  LogOut, 
  ShieldCheck, 
  ChevronDown, 
  ChevronUp,
  Search,
  Download,
  AlertCircle,
  Trash2,
  FileDown,
  FileText as FileIcon
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { 
  Document, 
  Packer, 
  Paragraph, 
  TextRun, 
  Table, 
  TableRow, 
  TableCell, 
  WidthType,
  AlignmentType,
  HeadingLevel,
  ImageRun,
  PageOrientation
} from 'docx';
import { saveAs } from 'file-saver';
import { 
  auth, 
  db, 
  googleProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc
} from '../firebase';

interface Commitment {
  id: string;
  parentName: string;
  childName: string;
  startDate: string;
  endDate: string;
  reflections: string[];
  signature: string;
  createdAt: any;
}

const ADMIN_EMAILS = ["graceushuang@gmail.com", "fbywinnie@gmail.com", "another-admin@gmail.com"];

const isAdmin = (email: string | null | undefined) => {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

export default function AdminDashboard() {
  const [user, setUser] = useState<any>(null);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const singlePdfRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u && isAdmin(u.email)) {
        setError(null);
        const q = query(collection(db, 'commitments'), orderBy('createdAt', 'desc'));
        const unsubscribeDocs = onSnapshot(q, (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Commitment[];
          setCommitments(data);
          setLoading(false);
        }, (err) => {
          console.error("Firestore error:", err);
          setError(`讀取資料失敗: ${err.message}`);
          setLoading(false);
        });
        return () => unsubscribeDocs();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = () => signOut(auth);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the card when clicking delete
    if (!window.confirm('確定要刪除這筆紀錄嗎？此動作無法復原。')) return;

    try {
      await deleteDoc(doc(db, 'commitments', id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert('刪除失敗，請稍後再試。');
    }
  };

  const downloadCSV = () => {
    if (commitments.length === 0) return;

    const headers = ['提交時間', '家長姓名', '孩子姓名', '開始日期', '結束日期', '心得1', '心得2', '心得3', '心得4'];
    const rows = commitments.map(c => [
      c.createdAt?.toDate().toLocaleString('zh-TW'),
      c.parentName,
      c.childName,
      c.startDate,
      c.endDate,
      ...(c.reflections.map(r => `"${r.replace(/"/g, '""')}"`))
    ]);

    const csvContent = "\uFEFF" + [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `家長約定紀錄_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    if (commitments.length === 0) return;

    const doc = new jsPDF();
    
    // Note: Standard jsPDF fonts don't support Chinese. 
    // For a production app, we would need to embed a custom font.
    // Here we'll try to use a standard font and provide a warning if it's likely to fail.
    
    doc.setFontSize(18);
    doc.text('Parent Commitment Records', 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Exported on: ${new Date().toLocaleString()}`, 14, 30);

    const tableData = commitments.map(c => [
      c.createdAt?.toDate().toLocaleString('zh-TW'),
      c.parentName,
      c.childName,
      `${c.startDate} ~ ${c.endDate}`,
      c.reflections.join('\n\n')
    ]);

    autoTable(doc, {
      startY: 35,
      head: [['Time', 'Parent', 'Child', 'Period', 'Reflections']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        4: { cellWidth: 60 }
      },
      didDrawCell: (data) => {
        // Add signature image if it's the last row of a commitment or we can add a new column
        // For simplicity in PDF (which has font issues anyway), let's just add the text "Signed" 
        // and focus on making the Word export perfect with the actual image.
      }
    });

    // Add signatures at the end of PDF as a separate section since autoTable images are complex
    let yPos = (doc as any).lastAutoTable.finalY + 10;
    commitments.forEach((c, i) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      if (c.signature) {
        try {
          doc.setFontSize(8);
          doc.text(`Signature for ${c.parentName}:`, 14, yPos);
          doc.addImage(c.signature, 'PNG', 14, yPos + 2, 30, 15);
          yPos += 25;
        } catch (e) {
          console.error("PDF Image error", e);
        }
      }
    });

    doc.save(`Commitment_Records_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const downloadSinglePDF = async (c: Commitment) => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    
    // Create a temporary container for rendering
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.padding = '40px';
    container.style.fontFamily = 'serif';
    
    container.innerHTML = `
      <div style="text-align: center; border-bottom: 2px solid #78350f; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="color: #78350f; margin: 0; font-size: 28px;">家長約定承諾書</h1>
        <p style="color: #92400e; font-style: italic; margin-top: 10px;">「父母的話語能夠賦予孩子能力、勇氣和智慧。」</p>
      </div>
      <div style="margin-bottom: 30px; font-size: 18px; line-height: 1.8;">
        <p><strong>家長姓名：</strong> ${c.parentName}</p>
        <p><strong>孩子姓名：</strong> ${c.childName}</p>
        <p><strong>約定期間：</strong> ${c.startDate} 至 ${c.endDate}</p>
      </div>
      <div style="margin-bottom: 30px;">
        <h3 style="color: #78350f; border-left: 4px solid #d97706; padding-left: 10px; margin-bottom: 15px;">思想與期待</h3>
        ${c.reflections.map((ref, i) => `
          <div style="margin-bottom: 15px; padding-left: 15px; border-left: 1px solid #f3f4f6;">
            <p style="font-size: 12px; color: #9ca3af; margin: 0;">問題 ${i + 1}</p>
            <p style="margin: 5px 0; font-size: 16px; white-space: pre-wrap;">${ref || '(未填寫)'}</p>
          </div>
        `).join('')}
      </div>
      <div style="margin-top: 50px; text-align: right;">
        <p style="font-weight: bold; color: #78350f; margin-bottom: 10px;">承諾人簽名：</p>
        <div style="display: inline-block; border-bottom: 2px solid #d1d5db; min-width: 200px; min-height: 100px;">
          ${c.signature ? `<img src="${c.signature}" style="max-height: 100px; display: block; margin: 0 auto;" />` : ''}
        </div>
        <p style="font-size: 14px; color: #9ca3af; margin-top: 10px;">提交時間：${c.createdAt?.toDate().toLocaleString('zh-TW')}</p>
      </div>
    `;
    
    document.body.appendChild(container);

    try {
      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`約定書_${c.parentName}_${c.childName}.pdf`);
    } catch (err) {
      console.error("Single PDF generation failed:", err);
      alert("PDF 生成失敗，請嘗試下載 Word 版本。");
    } finally {
      document.body.removeChild(container);
      setIsGeneratingPDF(false);
    }
  };

  const downloadDocx = async () => {
    if (commitments.length === 0) return;

    const tableRows = await Promise.all(commitments.map(async (c) => {
      let signatureImage: ImageRun | Paragraph = new Paragraph("無簽名");
      
      if (c.signature) {
        try {
          const base64Data = c.signature.split(',')[1];
          const binaryData = Uint8Array.from(atob(base64Data), char => char.charCodeAt(0));
          signatureImage = new Paragraph({
            children: [
              new ImageRun({
                data: binaryData,
                transformation: {
                  width: 100,
                  height: 50,
                },
              } as any),
            ],
          });
        } catch (e) {
          console.error("Docx Image error", e);
        }
      }

      return new TableRow({
        children: [
          new TableCell({ 
            children: [new Paragraph(c.createdAt?.toDate().toLocaleString('zh-TW'))],
            verticalAlign: AlignmentType.CENTER,
          }),
          new TableCell({ 
            children: [new Paragraph(c.parentName)],
            verticalAlign: AlignmentType.CENTER,
          }),
          new TableCell({ 
            children: [new Paragraph(c.childName)],
            verticalAlign: AlignmentType.CENTER,
          }),
          new TableCell({ 
            children: [new Paragraph(`${c.startDate} ~ ${c.endDate}`)],
            verticalAlign: AlignmentType.CENTER,
          }),
          new TableCell({ 
            children: [new Paragraph({ text: c.reflections.join('\n\n'), spacing: { before: 100, after: 100 } })],
          }),
          new TableCell({ 
            children: [signatureImage],
            verticalAlign: AlignmentType.CENTER,
          }),
        ],
      });
    }));

    const doc = new Document({
      sections: [{
        properties: {
          page: {
            size: {
              orientation: PageOrientation.LANDSCAPE,
            },
          },
        },
        children: [
          new Paragraph({
            text: "家長約定紀錄報表 (含簽名)",
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),
          new Paragraph({
            text: `匯出時間: ${new Date().toLocaleString('zh-TW')}`,
            alignment: AlignmentType.RIGHT,
            spacing: { after: 200 },
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            columnWidths: [1500, 1000, 1000, 1500, 3500, 1500], // Proportional widths
            rows: [
              new TableRow({
                children: [
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "提交時間", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "家長姓名", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "孩子姓名", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "約定期間", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "心得記錄", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                  new TableCell({ 
                    children: [new Paragraph({ children: [new TextRun({ text: "電子簽名", bold: true })], alignment: AlignmentType.CENTER })],
                    shading: { fill: "F3F4F6" },
                  }),
                ],
              }),
              ...tableRows
            ],
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `家長約定紀錄_${new Date().toISOString().split('T')[0]}.docx`);
  };

  const downloadSingleDocx = async (c: Commitment) => {
    let signatureImage: ImageRun | Paragraph = new Paragraph("無簽名");
    
    if (c.signature) {
      try {
        const base64Data = c.signature.split(',')[1];
        const binaryData = Uint8Array.from(atob(base64Data), char => char.charCodeAt(0));
        signatureImage = new Paragraph({
          children: [
            new ImageRun({
              data: binaryData,
              transformation: {
                width: 200,
                height: 100,
              },
            } as any),
          ],
        });
      } catch (e) {
        console.error("Docx Image error", e);
      }
    }

    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({
            text: "家長約定承諾書",
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "家長姓名：", bold: true }),
              new TextRun(c.parentName),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "孩子姓名：", bold: true }),
              new TextRun(c.childName),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "約定期間：", bold: true }),
              new TextRun(`${c.startDate} ~ ${c.endDate}`),
            ],
            spacing: { after: 400 },
          }),
          new Paragraph({
            text: "思想與期待：",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          ...c.reflections.map((ref, i) => new Paragraph({
            children: [
              new TextRun({ text: `問題 ${i + 1}：`, bold: true }),
              new TextRun({ text: `\n${ref || '(未填寫)'}`, break: 1 }),
            ],
            spacing: { after: 300 },
          })),
          new Paragraph({
            text: "承諾簽名：",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          signatureImage,
          new Paragraph({
            text: `提交時間：${c.createdAt?.toDate().toLocaleString('zh-TW')}`,
            alignment: AlignmentType.RIGHT,
            spacing: { before: 800 },
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `約定書_${c.parentName}_${c.childName}.docx`);
  };

  const filteredCommitments = commitments.filter(c => 
    c.parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.childName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!user || !isAdmin(user.email)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50 p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white p-8 rounded-3xl shadow-xl text-center space-y-6"
        >
          <ShieldCheck className="w-16 h-16 text-amber-600 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">管理員登入</h2>
          <p className="text-gray-600">此頁面僅限管理員存取，請使用授權的 Google 帳號登入。</p>
          <button 
            onClick={handleLogin}
            className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors font-medium"
          >
            <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
            使用 Google 登入
          </button>
          {user && (
            <div className="p-4 bg-rose-50 rounded-xl text-rose-700 text-sm space-y-1">
              <p className="font-bold">目前登入帳號：</p>
              <p className="break-all">{user.email}</p>
              {!isAdmin(user.email) && (
                <p className="mt-2 text-rose-600">⚠️ 此帳號未在授權清單中。</p>
              )}
              {user.email && isAdmin(user.email) && !user.emailVerified && (
                <p className="mt-2 text-rose-600">⚠️ 您的 Email 尚未驗證，請先完成驗證。</p>
              )}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-600 text-white rounded-2xl shadow-lg">
              <Users size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">管理後台</h1>
              <p className="text-gray-500 text-sm">目前共有 {commitments.length} 份約定紀錄</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-rose-600 transition-colors"
              title="登出"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>

        {/* Search & Stats */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text"
              placeholder="搜尋家長或孩子姓名..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-amber-500 outline-none transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={downloadCSV}
              className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors text-sm font-medium"
            >
              <Download size={18} />
              CSV
            </button>
            <button 
              onClick={downloadDocx}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <FileIcon size={18} />
              Word
            </button>
            <button 
              onClick={downloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-700 rounded-xl hover:bg-rose-100 transition-colors text-sm font-medium"
            >
              <FileDown size={18} />
              PDF
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl text-rose-700 text-sm flex items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        {/* List */}
        <div className="space-y-4">
          {filteredCommitments.map((c) => (
            <div 
              key={c.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md"
            >
              <div 
                className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-lg">
                    {c.parentName[0]}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{c.parentName} <span className="text-gray-400 font-normal mx-2">為</span> {c.childName}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar size={14} /> {c.startDate} ~ {c.endDate}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-400 hidden lg:block">
                    {c.createdAt?.toDate().toLocaleString('zh-TW')}
                  </span>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={(e) => { e.stopPropagation(); downloadSingleDocx(c); }}
                      className="p-2 text-blue-400 hover:text-blue-600 transition-colors"
                      title="下載 Word"
                    >
                      <FileIcon size={18} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); downloadSinglePDF(c); }}
                      className="p-2 text-rose-400 hover:text-rose-600 transition-colors"
                      title="下載 PDF"
                    >
                      <FileDown size={18} />
                    </button>
                  </div>
                  <button 
                    onClick={(e) => handleDelete(c.id, e)}
                    className="p-2 text-gray-300 hover:text-rose-500 transition-colors"
                    title="刪除紀錄"
                  >
                    <Trash2 size={18} />
                  </button>
                  {expandedId === c.id ? <ChevronUp className="text-gray-400" /> : <ChevronDown className="text-gray-400" />}
                </div>
              </div>

              {expandedId === c.id && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-6 pb-6 border-t border-gray-50 pt-6 space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Reflections */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                        <FileText size={16} /> 思想與期待
                      </h4>
                      <div className="space-y-4">
                        {c.reflections.map((ref, i) => (
                          <div key={i} className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 mb-1">問題 {i + 1}</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{ref || '(未填寫)'}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Signature */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                          <ShieldCheck size={16} /> 承諾簽名
                        </h4>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => downloadSingleDocx(c)}
                            className="text-xs flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <FileIcon size={14} /> Word
                          </button>
                          <button 
                            onClick={() => downloadSinglePDF(c)}
                            className="text-xs flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-colors"
                          >
                            <FileDown size={14} /> PDF
                          </button>
                        </div>
                      </div>
                      <div className="border border-amber-100 rounded-xl p-4 bg-amber-50/30 flex flex-col items-center justify-center min-h-[128px] relative group">
                        {c.signature ? (
                          <>
                            <img 
                              src={c.signature} 
                              alt="Signature" 
                              className="max-h-32 object-contain"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                console.error("Signature image failed to load");
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                              <a 
                                href={c.signature} 
                                download={`signature_${c.parentName}.png`}
                                className="p-2 bg-white rounded-full shadow-lg text-amber-600 hover:text-amber-800 transition-colors"
                                title="下載簽名圖檔"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download size={20} />
                              </a>
                            </div>
                            <p className="text-[10px] text-gray-300 mt-2">簽名長度: {c.signature.length} 字元</p>
                          </>
                        ) : (
                          <div className="text-gray-400 text-sm flex flex-col items-center gap-2">
                            <AlertCircle size={24} />
                            <span>無簽名資料</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          ))}

          {filteredCommitments.length === 0 && (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
              <p className="text-gray-400">找不到相符的紀錄</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
